import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useChatChannels, ChatChannel } from './useChatChannels';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import PageHeader from '../PageHeader';
import { Avatar, Icon, Popover } from '../ui';

const OWLBERT_USER_ID = 'a0000000-0000-0000-0000-000000000001';

interface SearchResult {
  id: string;
  channel_id: string;
  user_id: string;
  content: string;
  created_at: string;
  user_name: string;
  user_avatar: string | null;
  channel_name: string;
  channel_type: string;
}

// Lightweight user cache for search results
const searchUserCache = new Map<string, { name: string; avatar: string | null }>();

interface ChatPageHeaderProps {
  onSearchSelect: (channelId: string) => void;
}

export default function ChatPageHeader({ onSearchSelect }: ChatPageHeaderProps) {
  const { channels, totalUnreadCount } = useChatChannels();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Build channel lookup map
  const channelMap = useRef(new Map<string, ChatChannel>());
  useEffect(() => {
    const map = new Map<string, ChatChannel>();
    channels.forEach(ch => map.set(ch.id, ch));
    channelMap.current = map;
  }, [channels]);

  // Debounced search
  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('id, channel_id, user_id, content, created_at')
        .ilike('content', `%${searchQuery.trim()}%`)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      if (!data || data.length === 0) {
        setResults([]);
        setShowResults(true);
        setLoading(false);
        return;
      }

      // Fetch user info for unique user_ids not in cache
      const uniqueUserIds = [...new Set(data.map(m => m.user_id))];
      const uncachedIds = uniqueUserIds.filter(id => !searchUserCache.has(id));

      if (uncachedIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, name, avatar')
          .in('id', uncachedIds);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (users || []).forEach((u: any) => {
          searchUserCache.set(u.id, { name: u.name || 'Unknown', avatar: u.avatar || null });
        });
      }

      // Enrich results
      const enriched: SearchResult[] = data.map(msg => {
        const userInfo = searchUserCache.get(msg.user_id) || { name: 'Unknown', avatar: null };
        const channel = channelMap.current.get(msg.channel_id);
        return {
          id: msg.id,
          channel_id: msg.channel_id,
          user_id: msg.user_id,
          content: msg.content,
          created_at: msg.created_at,
          user_name: userInfo.name,
          user_avatar: userInfo.avatar,
          channel_name: channel?.name || 'Unknown',
          channel_type: channel?.type || 'general',
        };
      });

      setResults(enriched);
      setShowResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }
    debounceRef.current = setTimeout(() => performSearch(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, performSearch]);

  const handleSelect = (channelId: string) => {
    onSearchSelect(channelId);
    setQuery('');
    setResults([]);
    setShowResults(false);
    inputRef.current?.blur();
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    inputRef.current?.focus();
  };

  // Highlight matching text in results
  const highlightMatch = (text: string, q: string) => {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + q.length);
    const after = text.slice(idx + q.length);
    return (
      <>
        {before}
        <mark
          style={{
            background: 'var(--status-warn-soft)',
            color: 'var(--status-warn)',
            borderRadius: '2px',
            padding: '0 2px',
          }}
        >
          {match}
        </mark>
        {after}
      </>
    );
  };

  const renderUserAvatar = (result: SearchResult) => {
    if (result.user_id === OWLBERT_USER_ID) {
      return (
        <span className="w-7 h-7 flex items-center justify-center flex-shrink-0">
          <span style={{ fontSize: '14px' }}>🦉</span>
        </span>
      );
    }

    return <Avatar name={result.user_name} src={result.user_avatar} size={28} />;
  };

  return (
    <PageHeader title="Chat" count={totalUnreadCount}>
      {/* Message search with results popover */}
      <div ref={containerRef} className="relative w-full md:w-[260px]">
        <Icon
          name="search"
          size={15}
          style={{
            position: 'absolute',
            left: '11px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: isFocused ? 'var(--accent-text)' : 'var(--text-faint)',
            pointerEvents: 'none',
            transition: 'color var(--dur-base) var(--ease-out)',
          }}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => {
            setIsFocused(true);
            if (results.length > 0 || query.trim().length >= 2) setShowResults(true);
          }}
          placeholder="Search messages..."
          style={{
            width: '100%',
            height: 'var(--control-sm)',
            padding: '0 56px 0 34px',
            background: isFocused ? 'var(--surface-raised)' : 'var(--surface-input)',
            border: isFocused
              ? '1px solid var(--border-focus)'
              : '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-body)',
            outline: 'none',
            transition: 'border-color var(--dur-base) var(--ease-out), background var(--dur-base) var(--ease-out)',
          }}
        />
        {loading && (
          <Loader2
            className="w-3.5 h-3.5 animate-spin"
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-faint)',
            }}
          />
        )}
        {query && !loading && (
          <button
            onClick={clearSearch}
            className="hover-wash pressable"
            style={{
              position: 'absolute',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              border: 'none',
              background: 'transparent',
              borderRadius: 'var(--radius-xs)',
              color: 'var(--text-faint)',
              cursor: 'pointer',
            }}
            title="Clear search"
          >
            <Icon name="x" size={13} />
          </button>
        )}

        {/* Search results dropdown */}
        <Popover
          open={showResults && (isFocused || query.length >= 2)}
          anchorEl={containerRef.current}
          onClose={() => {
            setShowResults(false);
            setIsFocused(false);
          }}
          matchWidth
          offset={8}
          minWidth={320}
          style={{ padding: 0, overflow: 'hidden' }}
        >
            {results.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                  {loading ? 'Searching...' : 'No messages found'}
                </p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto chat-scrollbar">
                <div
                  className="px-3 py-2"
                  style={{ borderBottom: '1px solid var(--border-hairline)' }}
                >
                  <span className="t-eyebrow">
                    {results.length} result{results.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {results.map(result => (
                  <button
                    key={result.id}
                    onClick={() => handleSelect(result.channel_id)}
                    className="hover-wash pressable w-full text-left px-3 py-2.5 flex items-start gap-2.5"
                    style={{
                      border: 'none',
                      borderBottom: '1px solid var(--border-hairline)',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    {renderUserAvatar(result)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className="truncate"
                          style={{
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                            color: 'var(--text-body)',
                          }}
                        >
                          {result.user_name}
                        </span>
                        <span
                          className="flex items-center gap-1 flex-shrink-0"
                          style={{
                            padding: '1px 7px',
                            borderRadius: 'var(--radius-pill)',
                            background: 'var(--surface-active)',
                            fontSize: 'var(--text-2xs)',
                            color: 'var(--text-muted)',
                          }}
                        >
                          <Icon
                            name={result.channel_type === 'direct' ? 'user' : 'messages-square'}
                            size={10}
                          />
                          {result.channel_name}
                        </span>
                        <span
                          className="t-num flex-shrink-0"
                          style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}
                        >
                          {formatDistanceToNow(new Date(result.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p
                        className="line-clamp-2"
                        style={{
                          margin: 0,
                          fontSize: 'var(--text-xs)',
                          lineHeight: 'var(--leading-normal)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {highlightMatch(
                          result.content.length > 200
                            ? result.content.substring(0, 200) + '...'
                            : result.content,
                          query
                        )}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
        </Popover>
      </div>
    </PageHeader>
  );
}
